import React, { Component } from 'react';
import PropTypes from 'prop-types';
import IconFilterAlt from '@cimpress-technology/react-streamline-icons/lib/IconFilterAlt';
import { css } from 'emotion';
import { Accordion, Select, SelectWrapper, Tooltip } from '@cimpress/react-components';
import get from 'lodash/get';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import compact from 'lodash/compact';
import { withTranslation } from 'react-i18next';
import TermStatus from '../../constants/termStatus';
import { TermType } from '../../constants/termTypes';
import Button from '../common/Button';
import { getLabelForContractType } from '../../utilities/displayFunctions';
import { actions as homeActions, selectors as homeSelectors } from '../../reducers/home';
import { connect } from 'react-redux';

const accordionStyle = css`
  border: 0 !important;
  &.accordion-open > :last-child {
    overflow: visible !important;
    & > :first-child {
      display: flex;
      flex-direction: column;
      overflow: visible !important;
    }
  }
`;

class FilterOptions extends Component {
  static defaultProps = {};

  static propTypes = {
    buyers: PropTypes.array.isRequired,
    sellers: PropTypes.array.isRequired,
    showTerminated: PropTypes.bool
  };

  state = {
    previouslyChangedFilterName: null
  };

  onChangeFilter = (e, logicMap, name) => {
    const { applyFilters, appliedFilters } = this.props;

    this.setState({ previouslyChangedFilterName: name }, () => applyFilters({ ...appliedFilters, ...e }, logicMap));
  };

  stringify = (t, o) => {
    if (isString(o)) {
      return <b>{o}</b>;
    }

    if (isArray(o)) {
      const LIMIT = 3;
      const firstFew = o.slice(0, LIMIT);
      const rest = o.slice(LIMIT);
      const strs = firstFew.map(e => <b key={e}>{e}</b>);
      if (rest.length) {
        strs.push(
          <Tooltip key="rest_tooltip" contents={<div>{rest.map(filter => <div key={filter}>{filter}</div>)}</div>}>
            {rest.length} more...
          </Tooltip>
        );
      }
      return strs.reduce((prev, curr) => [...prev, ` ${t('common:commonWords:orText')} `, curr], []).slice(1);
    }

    if (isObject(o)) {
      return JSON.stringify(o);
    }

    return o;
  };

  onClearFilter = e => {
    e.stopPropagation();
    const { clearFilters } = this.props;

    this.setState({ previouslyChangedFilterName: undefined }, clearFilters);
  };

  render() {
    const { previouslyChangedFilterName } = this.state;
    const { t, buyers, sellers, terms, showTerminated, appliedFilters } = this.props;

    /*
    supportedFilter properties
    {
      name (string, required): The id of the filter. This should be unique.
      label (string, required): The label that should be displayed in the component.
      options ([{value: (string, required), label: (string, required), ...otherProps}], required): The available options
      multi (bool): Flag for multi select
      logic (string OR func(term, selectedOption(s))): The filter logic. If multi is set to true, the logic will be given all selected options. If it is not set to true, it will be given the selected option.
                                                       If string is provided, it should be a selector to the term object (e.g. 'modifiers.gracePeriodConfiguration').
                                                       If function is provided (for custom logic), the first argument will be a term object and second will be the selected option/options.
     }
     */
    const supportedFilters = [
      {
        name: 'seller',
        label: t('common:commonTerminology.seller', { context: 'oneOrMore' }),
        options: sellers,
        multi: true,
        logic: (term, selectedOptions) =>
          selectedOptions.some(({ id, type }) => id === term.seller.id && type === term.seller.type)
      },
      {
        name: 'buyer',
        label: t('common:commonTerminology.buyer', { context: 'oneOrMore' }),
        options: buyers,
        multi: true,
        logic: (term, selectedOptions) =>
          selectedOptions.some(({ id, type }) => id === term.buyer.id && type === term.buyer.type)
      },
      {
        name: 'status',
        label: t('common:commonTerminology.status', { context: 'oneOrMore' }),
        options: compact([
          { value: TermStatus.APPROVED, label: t('common:contractStatus.approved') },
          { value: TermStatus.PENDING, label: t('common:contractStatus.pending') },
          showTerminated && { value: TermStatus.TERMINATED, label: t('common:contractStatus.terminated') }
        ]),
        multi: true,
        logic: 'status'
      },
      {
        name: 'contractType',
        label: t('common:commonTerminology.contractType', { context: 'oneOrMore' }),
        options: [
          { value: TermType.LIST_PRICE, label: getLabelForContractType(TermType.LIST_PRICE) },
          { value: TermType.SPECIAL_PRICE, label: getLabelForContractType(TermType.SPECIAL_PRICE) },
          { value: TermType.FREE_SHIPPING, label: getLabelForContractType(TermType.FREE_SHIPPING) },
          { value: TermType.SHIPPING_COST, label: getLabelForContractType(TermType.SHIPPING_COST) },
          { value: TermType.SHIPPING_PRICE, label: getLabelForContractType(TermType.SHIPPING_PRICE) }
        ],
        multi: true,
        logic: 'type'
      },
      {
        name: 'hasGracePeriod',
        label: t('common:commonTerminology.hasGracePeriod'),
        options: [
          { value: true, label: t('common:commonWords:yesText') },
          { value: false, label: t('common:commonWords:noText') }
        ],
        logic: (term, selectedOption) => {
          return !!get(term, 'modifiers.gracePeriodConfiguration') === selectedOption.value;
        }
      }
    ];

    const logicMap = supportedFilters.reduce((map, { name, logic, multi }) => {
      if (!(isFunction(logic) || isString(logic))) {
        console.warn(`The logic for '${name}' should be a function or a term selector string`);
        return map;
      }

      let filterLogic = logic;
      // the filtering logic is simple if it's not a function.
      if (!(logic instanceof Function)) {
        if (multi) {
          filterLogic = (term, selectedOptions) => selectedOptions.some(({ value }) => get(term, logic) === value);
        } else {
          filterLogic = (term, selectedOption) => get(term, logic) === selectedOption.value;
        }
      }

      map[name] = (term, selected) => (!selected || isEmpty(selected) ? true : filterLogic(term, selected));

      return map;
    }, {});

    const precalculatedFilterCountsMap = {};

    // go through and precalculate all possible counts for each filter options and mutate the fields with new 'count' and 'disabled' property.
    // TODO: watch out for performance. This will run for complexity of O(terms * sum(filterOptions)).
    terms.forEach(term => {
      // attempt to reduce the time complexity a bit by skipping the recalculation of previously changed filter.
      const filtersToRecalculate = supportedFilters.filter(f => f.name !== previouslyChangedFilterName);
      filtersToRecalculate.reduce((map, filter) => {
        filter.options.forEach(o => {
          // keep other filters, and just replace the current filter name with the current option.
          const simulatedFilters = { ...appliedFilters, [filter.name]: filter.multi ? [o] : o };
          if (!map[filter.name]) {
            map[filter.name] = {};
          }

          if (map[filter.name][o.value] === undefined) {
            map[filter.name][o.value] = 0;
            o.count = map[filter.name][o.value];
            o.disabled = true;
          }

          const filterLogics = Object.keys(logicMap);

          if (filterLogics.every(filterName => logicMap[filterName](term, simulatedFilters[filterName]))) {
            map[filter.name][o.value] += 1;
            o.count = map[filter.name][o.value];
            o.disabled = false;
          }
        });

        return map;
      }, precalculatedFilterCountsMap);
    });

    const FilterComponents = compact(
      supportedFilters.map(({ name, label, options = [], multi, logic }) => {
        if (!name || !logic) {
          console.warn('name and logic properties are required.');
          return null;
        }

        return (
          <div className="col-sm-12 col-md-4" key={name}>
            <SelectWrapper
              selectedSelect={Select}
              label={label}
              value={appliedFilters[name]}
              options={options}
              onChange={e => this.onChangeFilter({ [name]: e }, logicMap, name)}
              multi={multi}
            />
          </div>
        );
      })
    );

    const clearFilter = (
      <Button bsStyle="link" onClick={this.onClearFilter}>
        ({t('contractsFilter:clearFilterLabel')})
      </Button>
    );

    const filteringOnText = compact(
      Object.keys(appliedFilters).map(name => {
        const selectedOptions = appliedFilters[name];

        if (!selectedOptions || isEmpty(selectedOptions)) {
          return null;
        }
        let selected;
        let count = 1;
        if (isArray(selectedOptions)) {
          selected = selectedOptions.map(option => option.label);
          count = selectedOptions.length;
        } else {
          selected = selectedOptions.label;
        }
        return (
          <div key={name}>
            {t(`common:commonTerminology.${name}`, { count })}: {this.stringify(t, selected)}
          </div>
        );
      })
    );

    const filterOffLabel = t('contractsFilter:filterOffLabel');
    const filterOnLabel = (
      <span>
        {t('contractsFilter:filterOnLabel')} {clearFilter} {filteringOnText}
      </span>
    );

    return (
      <Accordion
        title={
          <span style={{ marginBottom: '5px' }}>
            <IconFilterAlt />
            <span style={{ marginLeft: '5px' }}>{filteringOnText.length ? filterOnLabel : filterOffLabel}</span>
          </span>
        }
        className={accordionStyle}
        headerStyle={{ background: '#ffffff' }}
        bodyStyle={{ padding: '0' }}>
        {FilterComponents}
      </Accordion>
    );
  }
}

const mapStateToProps = state => ({
  appliedFilters: homeSelectors.getAppliedFilters(state)
});

const mapDispatchToProps = {
  applyFilters: homeActions.applyFilters,
  clearFilters: homeActions.clearFilters
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(FilterOptions));
